import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Typography, 
  Grid, 
  Divider, 
  Box, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Stack,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
  Description as DocumentIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';


const ViewVendor = () => {
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const parsedToken = JSON.parse(atob(token.split('.')[1])); // Decode JWT token
        setUsername(parsedToken.username); // Set username from the token
      } catch (error) {
        console.error('Failed to parse token:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchVendorData = async () => {
      try {
        const vendorResponse = await axios.get(`https://apidashboard.eurekagroup.id/vendor/get/${id}`);
        setVendor(vendorResponse.data);

        const categoryResponse = await axios.get('https://apidashboard.eurekagroup.id/documents');
        setDocumentCategories(categoryResponse.data);

        const userResponse = await axios.get('https://apidashboard.eurekagroup.id/employee');
        setUsers(userResponse.data);
      } catch (error) {
        console.error("Error fetching data", error);
        setVendor(null);
      } finally {
        setLoading(false);
      }
    };

    fetchVendorData();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        gap: 3
      }}>
        <Box sx={{ position: 'relative' }}>
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ 
              color: 'primary.main',
              animationDuration: '550ms'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <BusinessIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          </Box>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Memuat Data Vendor
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Mohon tunggu sebentar...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!vendor) {
    return <Typography variant="body1" align="center" color="error">Vendor not found</Typography>;
  }

  const handleDocumentClick = (docId) => {
    navigate(`/document/ViewDocument/${docId}`);
  };

  const handleBackToVendor = () => {
    navigate('/kontak/vendor');
  };

  return (
    <Box className="animate-fade-in" sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={handleBackToVendor}
          sx={{ 
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { 
              bgcolor: 'primary.dark',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease',
            mr: 2
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Button 
          variant="contained" 
          color="info" 
          startIcon={<EditIcon />}
          onClick={() => navigate(`/kontak/vendor/edit/${vendor._id}`)}
          className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              transform: 'translateY(-2px)'
            }
          }}
        >
          ✏️ Edit Vendor
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Profile Card */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
            <CardHeader 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <BusinessIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Data Vendor
                  </Typography>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: 'primary.main',
                    fontSize: '2rem'
                  }}
                >
                  {vendor.vendor_name?.charAt(0)}
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  {vendor.vendor_name}
                </Typography>
                <Chip 
                  label={vendor.is_active === 1 ? 'Active' : 'Inactive'}
                  color={vendor.is_active === 1 ? 'success' : 'error'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email"
                    secondary={vendor.email || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Mobile"
                    secondary={vendor.mobile || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Business Unit"
                    secondary={vendor.id_bu?.bu_name || 'N/A'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Detail Cards */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            {/* PIC Information */}
            <Card sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)', borderRadius: 2 }}>
              <CardHeader 
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'grey.600', width: 32, height: 32 }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Informasi PIC
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      PIC Title
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.pic_title || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      PIC Last Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.pic_last_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      PIC Position
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.pic_position || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Internal PIC
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.internal_pic || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Business Information */}
            <Card sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)', borderRadius: 2 }}>
              <CardHeader 
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'grey.600', width: 32, height: 32 }}>
                      <BusinessIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Informasi Bisnis
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Business Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.business_type || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Term of Payment
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.term_of_payment || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Year Founded
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.year_founded || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Tax No
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.tax_no || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Bank Information */}
            <Card sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)', borderRadius: 2 }}>
              <CardHeader 
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'grey.600', width: 32, height: 32 }}>
                      <BankIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Informasi Bank
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Bank Account Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.bank_account_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Bank Account Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.bank_account_number || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Bank Account Holder
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.bank_account_nameholder || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Bank Account Branch
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.bank_account_brench || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Location Information */}
            <Card sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)', borderRadius: 2 }}>
              <CardHeader 
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'grey.600', width: 32, height: 32 }}>
                      <LocationIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Lokasi
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.address || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      City
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.id_city || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Province
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.id_province || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Country
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.id_country || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Documents */}
            <Card sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)', borderRadius: 2 }}>
              <CardHeader 
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'grey.600', width: 32, height: 32 }}>
                      <DocumentIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Dokumen ({vendor.document_count || 0})
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                {vendor.documents && vendor.documents.length > 0 ? (
                  <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>
                            Nama Dokumen
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>
                            Kategori
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>
                            Diupload Oleh
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>
                            Tanggal Upload
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', textAlign: 'center' }}>
                            Aksi
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vendor.documents.map((doc) => (
                          <TableRow key={doc._id} sx={{ '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' } }}>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {doc.document_name}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={doc.category_name || 'Unknown Category'}
                                variant="outlined"
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {doc.full_name || '-'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {new Date(doc.date_added).toLocaleDateString()}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Button 
                                variant="contained" 
                                color="primary" 
                                size="small"
                                onClick={() => handleDocumentClick(doc._id)}
                                sx={{ 
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 600
                                }}
                              >
                                📄 Lihat
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <DocumentIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      Belum ada dokumen yang diupload
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewVendor;
